// src/contexts/AuthContext.jsx - ИСПРАВЛЕННАЯ ВЕРСИЯ С ОЧИСТКОЙ INACTIVE_REASON
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKeyValid, setApiKeyValid] = useState(true);
  const [checkingTelegramAuth, setCheckingTelegramAuth] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Проверка неактивного аккаунта при загрузке
  useEffect(() => {
    const checkInactiveOnLoad = () => {
      const inactiveReason = localStorage.getItem('inactive_reason');
      const currentPath = window.location.pathname;
      
      if (currentPath === '/_admin/inactive') return;
      
      if (inactiveReason) {
        try {
          const reason = JSON.parse(inactiveReason);
          const timestamp = new Date(reason.timestamp);
          const now = new Date();
          
          // Если прошло больше 5 минут, очищаем
          if (now - timestamp > 5 * 60 * 1000) {
            console.log('🧹 Clearing expired inactive_reason');
            localStorage.removeItem('inactive_reason');
            return;
          }
          
          window.location.href = '/_admin/inactive';
        } catch {
          localStorage.removeItem('inactive_reason');
        }
      }
    };

    checkInactiveOnLoad();
  }, []);

  // Проверяем Telegram данные сразу при загрузке
  useEffect(() => {
    const checkTelegramAuthOnLoad = () => {
      const telegramData = localStorage.getItem('telegram_auth_data');
      if (telegramData && !user) {
        console.log('🔄 Found Telegram auth data, processing...');
        setCheckingTelegramAuth(true);
        handleStoredTelegramAuth(telegramData);
      }
    };

    if (!loading) {
      checkTelegramAuthOnLoad();
    }
  }, [loading, user]);

  const handleStoredTelegramAuth = async (telegramData) => {
    try {
      const parsedData = JSON.parse(telegramData);
      console.log('📨 Processing stored Telegram data:', parsedData);
      
      const result = await loginWithTelegram(parsedData);
      
      if (result.success) {
        localStorage.removeItem('telegram_auth_data');
        console.log('✅ Stored Telegram auth successful');
      } else {
        console.error('❌ Stored Telegram auth failed:', result.error);
        localStorage.removeItem('telegram_auth_data');
      }
    } catch (error) {
      console.error('❌ Stored Telegram auth error:', error);
      localStorage.removeItem('telegram_auth_data');
    } finally {
      setCheckingTelegramAuth(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const userData = await authAPI.getCurrentUser();
        
        // Проверяем активность пользователя
        if (userData && !userData.is_active) {
          console.log('🔴 Account is inactive during auth check');
          
          localStorage.setItem('inactive_reason', JSON.stringify({
            message: 'Ваш аккаунт деактивирован',
            timestamp: new Date().toISOString(),
            source: 'auth_check'
          }));
          
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          // window.location.href = '/_admin/inactive';
           setUser(null);
          return;
        }
        
        // ✅ Если пользователь активен, очищаем устаревший флаг
        if (userData && userData.is_active) {
          if (localStorage.getItem('inactive_reason')) {
            console.log('🧹 Clearing stale inactive_reason in checkAuth');
            localStorage.removeItem('inactive_reason');
          }
        }
        
        const enrichedUserData = {
          ...userData,
          name: userData.name || 'Пользователь',
          email: userData.email || '',
          role: userData.role || 'user',
          is_active: userData.is_active !== undefined ? userData.is_active : true,
          created_at: userData.created_at || null,
          updated_at: userData.updated_at || null
        };
        setUser(enrichedUserData);
        setApiKeyValid(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        
        if (error.response?.status === 403 && error.response?.data?.code === 'inactive_account') {
          console.log('🔴 Inactive account detected in auth check');
          
          localStorage.setItem('inactive_reason', JSON.stringify({
            message: error.response.data.message || 'Ваш аккаунт деактивирован',
            timestamp: new Date().toISOString(),
            source: 'auth_check_403'
          }));
          
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          // window.location.href = '/_admin/inactive';
           setUser(null);
          return;
        }
        
        if (error.response?.status === 403 && error.response?.data?.detail === 'Invalid API Key') {
          setApiKeyValid(false);
        } else if (error.response?.status === 401) {
          logout();
        } else {
          console.error('Auth check error:', error);
        }
      }
    }
    setLoading(false);
  };

  const extractFieldErrors = (errorData) => {
    const fieldErrors = {};
    
    if (errorData.detail && Array.isArray(errorData.detail)) {
      errorData.detail.forEach(error => {
        if (error.loc && error.loc[1]) {
          const fieldName = error.loc[1];
          fieldErrors[fieldName] = error.msg;
        }
      });
    }
    
    return fieldErrors;
  };

  const login = async (email, password, useJson = true) => {
    try {
      let response;
      if (useJson) {
        response = await authAPI.loginJson(email, password);
      } else {
        response = await authAPI.login(email, password);
      }

      const { access_token } = response;
      localStorage.setItem('access_token', access_token);
      
      const userData = await authAPI.getCurrentUser();
      
      // Проверяем активность после логина
      if (userData && !userData.is_active) {
        localStorage.setItem('inactive_reason', JSON.stringify({
          message: 'Ваш аккаунт деактивирован',
          timestamp: new Date().toISOString(),
          source: 'login'
        }));
        
        localStorage.removeItem('access_token');
        window.location.href = '/_admin/inactive';
        return { success: false, error: 'Аккаунт деактивирован' };
      }
      
      // ✅ Если пользователь активен, очищаем устаревший флаг
      if (userData && userData.is_active) {
        if (localStorage.getItem('inactive_reason')) {
          console.log('🧹 Clearing stale inactive_reason in login');
          localStorage.removeItem('inactive_reason');
        }
      }
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return { success: true };
    } catch (error) {
      console.error('Login error in context:', error);
      
      if (error.response?.status === 422) {
        const fieldErrors = extractFieldErrors(error.response.data);
        return { 
          success: false, 
          error: 'Ошибка валидации данных',
          fieldErrors: fieldErrors
        };
      }
      
      if (error.response?.status === 403 && error.response?.data?.code === 'inactive_account') {
        localStorage.setItem('inactive_reason', JSON.stringify({
          message: error.response.data.message || 'Ваш аккаунт деактивирован',
          timestamp: new Date().toISOString(),
          source: 'login_403'
        }));
        window.location.href = '/_admin/inactive';
        return { success: false, error: 'Аккаунт деактивирован' };
      }
      
      const message = error.response?.data?.detail || 
                     error.response?.data?.message || 
                     'Ошибка авторизации';
      return { success: false, error: message };
    }
  };

  const loginWithTelegram = async (telegramData) => {
    try {
      console.log('📨 Sending Telegram data to backend:', telegramData);
      
      const processedData = {
        id: parseInt(telegramData.id),
        first_name: telegramData.first_name,
        last_name: telegramData.last_name || '',
        username: telegramData.username || '',
        photo_url: telegramData.photo_url || '',
        auth_date: parseInt(telegramData.auth_date),
        hash: telegramData.hash
      };
      
      console.log('📤 Processed data for backend:', processedData);
      
      const response = await authAPI.verifyTelegramAuth(processedData);
      console.log('✅ Backend response:', response);
      
      const { access_token } = response;
      localStorage.setItem('access_token', access_token);
      
      const userData = await authAPI.getCurrentUser();
      
      // Проверяем активность после Telegram логина
      if (userData && !userData.is_active) {
        localStorage.setItem('inactive_reason', JSON.stringify({
          message: 'Ваш аккаунт деактивирован',
          timestamp: new Date().toISOString(),
          source: 'telegram_login'
        }));
        
        localStorage.removeItem('access_token');
        window.location.href = '/_admin/inactive';
        return { success: false, error: 'Аккаунт деактивирован' };
      }
      
      // ✅ Если пользователь активен, очищаем устаревший флаг
      if (userData && userData.is_active) {
        if (localStorage.getItem('inactive_reason')) {
          console.log('🧹 Clearing stale inactive_reason in telegram_login');
          localStorage.removeItem('inactive_reason');
        }
      }
      
      console.log('✅ User data received:', userData);
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return { success: true };
    } catch (error) {
      console.error('❌ Telegram auth error:', error);
      
      if (error.response?.status === 401 && 
          error.response?.data?.detail?.includes('inactive')) {
        localStorage.setItem('inactive_reason', JSON.stringify({
          message: 'Ваш аккаунт деактивирован',
          timestamp: new Date().toISOString(),
          source: 'telegram_401'
        }));
        window.location.href = '/_admin/inactive';
        return { success: false, error: 'Аккаунт деактивирован' };
      }
      
      const message = error.response?.data?.detail || 
                    error.response?.data?.message || 
                    'Telegram authentication failed';
      return { success: false, error: message };
    }
  };

  const getTelegramAuthUrl = async () => {
    try {
      const response = await authAPI.getTelegramAuthUrl();
      return { success: true, url: response.auth_url };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to get Telegram auth URL';
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password) => {
    try {
      await authAPI.register(name, email, password);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('telegram_auth_data');
    setUser(null);
  };

  const clearInactiveStatus = () => {
    console.log('🧹 Manually clearing inactive_reason');
    localStorage.removeItem('inactive_reason');
  };

  const value = {
    user,
    login,
    loginWithTelegram,
    getTelegramAuthUrl,
    register,
    logout,
    clearInactiveStatus,
    loading: loading || checkingTelegramAuth,
    isAuthenticated: !!user,
    apiKeyValid,
    checkingTelegramAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};